'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, ArrowLeft, UserPlus } from 'lucide-react';
import { LoginScreen } from './LoginScreen';
import { useTheme } from './ThemeProvider';
import { PATHS } from '../router/paths';

interface LoginPromptModalProps {
  open: boolean;
  /** Optional context heading, e.g. "Create an account to like posts". */
  label?: string;
  onClose: () => void;
}

/**
 * Signup-first auth prompt shown over the public YapTab preview. "Create account"
 * routes to the full signup flow; "Log in" swaps to the existing LoginScreen
 * inline so a returning visitor authenticates without leaving the feed (the
 * AuthGateProvider closes this modal automatically on auth-state change).
 */
export function LoginPromptModal({ open, label, onClose }: LoginPromptModalProps) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [mode, setMode] = useState<'prompt' | 'login'>('prompt');

  // Colors are set inline rather than with `dark:` utilities: this project ships a
  // pre-generated stylesheet (src/index.css is compiled Tailwind output, not a
  // source file), so arbitrary-value classes that weren't already in it — e.g.
  // dark:bg-[#161616] — produce no rule at all. That left this panel on its light
  // background while dark:text-white still applied, i.e. white text on cream.
  const isDark  = theme === 'dark';
  const surface = isDark ? '#1a1a1a' : '#FAF8F5';   // same surface other modals use
  const heading = isDark ? '#FFFFFF' : '#000000';
  const body    = isDark ? '#999999' : '#666666';

  // Always reopen on the signup-first prompt.
  useEffect(() => { if (open) setMode('prompt'); }, [open]);

  if (!open || typeof document === 'undefined') return null;

  const goToSignup = () => { onClose(); navigate(PATHS.SIGNUP); };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {mode === 'prompt' ? (
        <div
          className="relative z-[71] w-full max-w-md mx-4 rounded-3xl border border-black/10 dark:border-white/10 shadow-2xl p-6 text-center"
          style={{ backgroundColor: surface, color: heading }}
        >
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10"
            style={{ color: body }}
          >
            <X className="w-5 h-5" />
          </button>

          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-brand-gradient flex items-center justify-center">
            <UserPlus className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-xl mb-1" style={{ color: heading, fontWeight: 700 }}>
            {label || 'Create a free account to continue'}
          </h2>
          <p className="text-sm mb-5" style={{ color: body }}>
            Sign up to keep scrolling and to like, reply, and post.
          </p>

          <button
            onClick={goToSignup}
            className="w-full py-3 rounded-xl bg-brand-gradient text-white font-bold mb-3 active:scale-95 transition-transform"
          >
            Create account
          </button>
          <button
            onClick={() => setMode('login')}
            className="w-full py-3 rounded-xl border border-black/10 dark:border-white/15 text-black dark:text-white font-semibold active:scale-95 transition-transform"
          >
            Log in
          </button>
        </div>
      ) : (
        <div className="relative z-[71] w-full max-w-md mx-4 rounded-3xl overflow-hidden shadow-2xl h-[85vh] max-h-[720px]">
          <button
            onClick={() => setMode('prompt')}
            aria-label="Back"
            className="absolute top-3 left-3 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-black/30 text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-black/30 text-white"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-full h-full overflow-auto">
            <LoginScreen
              onLogin={() => { /* AuthGateProvider closes this modal on auth-state change */ }}
              onSignup={goToSignup}
              onSignupComplete={() => { onClose(); navigate(PATHS.PROFILE_SETUP); }}
            />
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
